import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { getViewers, addViewer, removeViewer, updateViewer } from '../utils/httpApi';
import './ViewerManagement.css';

const ViewerManagement = forwardRef(function ViewerManagement({ receiverUuid, token, onPendingChange }, ref) {
  const [viewers, setViewers] = useState([]);
  const [viewerCount, setViewerCount] = useState(0);
  const [maxViewers, setMaxViewers] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Add viewer form state
  const [newViewerEmail, setNewViewerEmail] = useState('');
  const [newViewerCanViewHistory, setNewViewerCanViewHistory] = useState(false);
  const [addingViewer, setAddingViewer] = useState(false);

  // Fetch viewers on mount and when receiverUuid changes
  const fetchViewers = useCallback(async () => {
    if (!receiverUuid || !token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getViewers(receiverUuid, token);

      if (response.header?.apiStatus === 'OK') {
        setViewers(response.viewers || []);
        setViewerCount(response.viewerCount || 0);
        setMaxViewers(response.maxViewers || 5);
      } else {
        const errors = response.header?.errors || [];
        setError(errors.map(e => e.message || e.errorMessage).join(', ') || 'Failed to load viewers');
      }
    } catch (err) {
      setError('Failed to load viewers');
      console.error('Error fetching viewers:', err);
    } finally {
      setLoading(false);
    }
  }, [receiverUuid, token]);

  useEffect(() => {
    fetchViewers();
  }, [fetchViewers]);

  // Clear success message after delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Adds the typed email as a viewer. Returns null on success, else the error shown.
  const addPendingViewer = useCallback(async () => {
    if (!newViewerEmail.trim()) {
      setError('Please enter an email address');
      return 'Please enter an email address';
    }

    setAddingViewer(true);
    setError(null);

    try {
      const response = await addViewer({
        receiverUuid,
        viewerEmail: newViewerEmail.trim(),
        canViewHistory: newViewerCanViewHistory
      }, token);

      if (response.header?.apiStatus === 'OK') {
        setNewViewerEmail('');
        setNewViewerCanViewHistory(false);
        setSuccessMessage('Viewer added successfully');
        fetchViewers();
        return null;
      }
      const errors = response.header?.errors || [];
      const message = errors.map(e => e.message || e.errorMessage).join(', ') || 'Failed to add viewer';
      setError(message);
      return message;
    } catch (err) {
      console.error('Error adding viewer:', err);
      setError('Failed to add viewer');
      return 'Failed to add viewer';
    } finally {
      setAddingViewer(false);
    }
  }, [newViewerEmail, newViewerCanViewHistory, receiverUuid, token, fetchViewers]);

  const handleAddViewer = (e) => {
    e.preventDefault();
    addPendingViewer();
  };

  // An email typed but not yet added is an unsaved change: the page's Save button
  // must add it, or the owner sees "saved" while the viewer was never shared with.
  const hasPendingViewer = newViewerEmail.trim() !== '';
  useEffect(() => {
    if (onPendingChange) onPendingChange(hasPendingViewer);
  }, [hasPendingViewer, onPendingChange]);
  useImperativeHandle(ref, () => ({
    addPendingViewer: () => (hasPendingViewer ? addPendingViewer() : Promise.resolve(null)),
  }), [hasPendingViewer, addPendingViewer]);

  const handleRemoveViewer = async (viewerId) => {
    if (!window.confirm('Are you sure you want to remove this viewer?')) {
      return;
    }

    setError(null);

    try {
      const response = await removeViewer(viewerId, receiverUuid, token);

      if (response.header?.apiStatus === 'OK') {
        setSuccessMessage('Viewer removed successfully');
        fetchViewers();
      } else {
        const errors = response.header?.errors || [];
        setError(errors.map(e => e.message || e.errorMessage).join(', ') || 'Failed to remove viewer');
      }
    } catch (err) {
      setError('Failed to remove viewer');
      console.error('Error removing viewer:', err);
    }
  };

  const handleToggleHistory = async (viewerId, currentValue) => {
    setError(null);

    try {
      const response = await updateViewer({
        viewerId,
        receiverUuid,
        canViewHistory: !currentValue
      }, token);

      if (response.header?.apiStatus === 'OK') {
        setSuccessMessage('Viewer updated successfully');
        fetchViewers();
      } else {
        const errors = response.header?.errors || [];
        setError(errors.map(e => e.message || e.errorMessage).join(', ') || 'Failed to update viewer');
      }
    } catch (err) {
      setError('Failed to update viewer');
      console.error('Error updating viewer:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hr${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const canAddMore = viewerCount < maxViewers;

  return (
    <div className="viewer-management">
      <div className="viewer-header">
        <h3>Shared Access</h3>
        <span className="viewer-count">{viewerCount}/{maxViewers} viewers</span>
      </div>

      {/* Success message */}
      {successMessage && (
        <div className="viewer-success-banner">
          {successMessage}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="viewer-error-banner">
          {error}
        </div>
      )}

      {/* Add viewer form */}
      {canAddMore && (
        <form className="add-viewer-form" onSubmit={handleAddViewer}>
          <div className="add-viewer-inputs">
            <input
              type="email"
              placeholder="Enter viewer's email address"
              value={newViewerEmail}
              onChange={(e) => setNewViewerEmail(e.target.value)}
              className="viewer-email-input"
              disabled={addingViewer}
            />
            <label className="history-checkbox-label">
              <input
                type="checkbox"
                checked={newViewerCanViewHistory}
                onChange={(e) => setNewViewerCanViewHistory(e.target.checked)}
                disabled={addingViewer}
              />
              <span>Allow History</span>
            </label>
            <button
              type="submit"
              className="btn-add-viewer"
              disabled={addingViewer || !newViewerEmail.trim()}
            >
              {addingViewer ? 'Adding...' : 'Add Viewer'}
            </button>
          </div>
          <p className="add-viewer-hint">
            Viewers can access your receiver's live map. They will need to log in with this email address.
          </p>
        </form>
      )}

      {!canAddMore && (
        <div className="viewer-limit-message">
          Maximum number of viewers reached ({maxViewers}).
        </div>
      )}

      {/* Viewers list */}
      {loading ? (
        <div className="viewer-loading">Loading viewers...</div>
      ) : viewers.length === 0 ? (
        <div className="no-viewers-message">
          No viewers added yet. Add a viewer to share access to this receiver.
        </div>
      ) : (
        <div className="viewers-table">
          <div className="viewers-table-header">
            <div className="viewer-col-email">Email</div>
            <div className="viewer-col-history">History</div>
            <div className="viewer-col-last-active">Last Active</div>
            <div className="viewer-col-actions">Actions</div>
          </div>
          {viewers.map((viewer) => (
            <div key={viewer.id} className="viewer-row">
              <div className="viewer-col-email">{viewer.viewerEmail}</div>
              <div className="viewer-col-history">
                <button
                  className={`history-toggle ${viewer.canViewHistory ? 'enabled' : 'disabled'}`}
                  onClick={() => handleToggleHistory(viewer.id, viewer.canViewHistory)}
                  title={viewer.canViewHistory ? 'Click to disable history access' : 'Click to enable history access'}
                >
                  {viewer.canViewHistory ? 'Yes' : 'No'}
                </button>
              </div>
              <div className="viewer-col-last-active">
                {formatDate(viewer.lastAccessedAt)}
              </div>
              <div className="viewer-col-actions">
                <button
                  className="btn-remove-viewer"
                  onClick={() => handleRemoveViewer(viewer.id)}
                  title="Remove viewer"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default ViewerManagement;
